import React from 'react'
import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import axios from 'axios'
import API_URL from '../../config'
import { setAuthToken } from '../../services/auth'

const ReconciliationDashboard = () => {
  const [stats, setStats] = useState({
    processedToday: 0,
    successRate: 0,
    avgProcessingTime: 0,
  })

  const [recentRecords, setRecentRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        // Set auth token for the request
        const token = localStorage.getItem('token')
        console.log('Token from localStorage:', token ? 'Token exists' : 'No token found')

        if (!token) {
          setError('Authentication token not found. Please log in again.')
          setLoading(false)
          return
        }

        setAuthToken(token)
        console.log('Authorization header:', axios.defaults.headers.common['Authorization'])

        // Fetch recent activity (last 10 records)
        console.log('Attempting to fetch recent records...')

        // Try with direct URL without relying on API_URL constant
        const baseUrl = 'http://localhost:8000/api'
        console.log('Using explicit base URL:', baseUrl)

        const recentResponse = await axios.get(`${baseUrl}/admin/reconciliation/recent/`, {
          params: {
            timeWindow: '24h',
            pageSize: 10,
            page: 1,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          withCredentials: true, // Include cookies in the request
          // Ensure request doesn't time out too quickly
          timeout: 10000,
        })
        console.log('Received recent records response:', recentResponse.data)

        setRecentRecords(recentResponse.data.results)

        // Calculate basic stats
        const successCount = recentResponse.data.results.filter(
          (record) => record.status === 'active' || record.status === 'complete'
        ).length

        // Use the current page records for success rate calculation
        const currentPageRecords = recentResponse.data.results.length
        const successRate = currentPageRecords > 0 ? (successCount / currentPageRecords) * 100 : 0

        // For processed today, we'll use the totalResults from the 24h query
        const processedToday = recentResponse.data.pagination.totalResults

        // Calculate average processing time (this is a placeholder - in reality you'd need timestamps from both start and end of processing)
        const avgProcessingTime = 2.3 // Placeholder value in seconds

        setStats({
          processedToday,
          successRate: Math.round(successRate),
          avgProcessingTime,
        })

        setLoading(false)
      } catch (err) {
        console.error('Error fetching reconciliation data:', err)
        setError('Failed to load reconciliation data. Please try again later.')
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // Function to render status icon
  const renderStatusIcon = (status) => {
    if (status === 'active' || status === 'complete') {
      return <span className='text-green-500'>✓</span>
    } else if (status === 'pending' || status === 'processing') {
      return <span className='text-yellow-500'>⟳</span>
    } else {
      return <span className='text-red-500'>✗</span>
    }
  }

  if (loading) {
    return <div className='p-4'>Loading reconciliation dashboard...</div>
  }

  if (error) {
    return <div className='text-red-500 p-4'>{error}</div>
  }

  return (
    <div className='p-4'>
      <h1 className='mb-6 text-2xl font-bold'>Reconciliation Dashboard</h1>

      {/* Navigation */}
      <div className='mb-6 flex border-b pb-2'>
        <Link to='/admin/reconciliation' className='px-4 py-2 font-bold'>
          Dashboard
        </Link>
        <Link to='/admin/reconciliation/recent' className='px-4 py-2'>
          Recent
        </Link>
        <Link to='/admin/reconciliation/search' className='px-4 py-2'>
          Search
        </Link>
        <Link to='/admin/reconciliation/manual' className='px-4 py-2'>
          Manual
        </Link>
      </div>

      {/* Stats */}
      <div className='mb-6'>
        <h2 className='mb-3 text-lg font-semibold'>RECONCILIATION STATS</h2>
        <div className='grid grid-cols-3 gap-4'>
          <div className='rounded border p-4 text-center'>
            <div className='text-gray-600'>Processed Today</div>
            <div className='text-2xl font-bold'>{stats.processedToday.toLocaleString()}</div>
          </div>
          <div className='rounded border p-4 text-center'>
            <div className='text-gray-600'>Success Rate</div>
            <div className='text-2xl font-bold'>{stats.successRate}%</div>
          </div>
          <div className='rounded border p-4 text-center'>
            <div className='text-gray-600'>Avg Processing</div>
            <div className='text-2xl font-bold'>{stats.avgProcessingTime} sec</div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className='mb-6'>
        <div className='mb-3 flex items-center justify-between'>
          <h2 className='text-lg font-semibold'>RECENT ACTIVITY</h2>
          <Link to='/admin/reconciliation/recent' className='text-blue-500 hover:underline'>
            View All &gt;
          </Link>
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full border'>
            <thead>
              <tr className='bg-gray-100'>
                <th className='border px-4 py-2'>Status</th>
                <th className='border px-4 py-2'>Type</th>
                <th className='border px-4 py-2'>Title</th>
                <th className='border px-4 py-2'>Artists</th>
                <th className='border px-4 py-2'>Service</th>
                <th className='border px-4 py-2'>Cloud ID</th>
                <th className='border px-4 py-2'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentRecords.length > 0 ? (
                recentRecords.map((record) => (
                  <tr
                    key={`${record.id}-${record.instanceId}-${record.service}`}
                    className='hover:bg-gray-50'
                  >
                    <td className='border px-4 py-2 text-center'>
                      {renderStatusIcon(record.status)}
                    </td>
                    <td className='border px-4 py-2 capitalize'>{record.mediaType || 'Unknown'}</td>
                    <td className='border px-4 py-2'>{record.title}</td>
                    <td className='border px-4 py-2'>{record.artists.join(', ')}</td>
                    <td className='border px-4 py-2'>{record.service || 'Unknown'}</td>
                    <td className='border px-4 py-2'>{record.cloudId || '-'}</td>
                    <td className='border px-4 py-2'>
                      <Link
                        to={`/admin/reconciliation/record/${record.id}`}
                        className='bg-blue-500 text-white hover:bg-blue-600 rounded px-2 py-1'
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan='7' className='border px-4 py-2 text-center'>
                    No recent records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reconciliation Performance Chart - Placeholder */}
      <div>
        <h2 className='mb-3 text-lg font-semibold'>RECONCILIATION PERFORMANCE</h2>
        <div className='bg-gray-50 flex h-64 items-center justify-center border p-4'>
          <p className='text-gray-500'>Performance chart would be displayed here</p>
        </div>
      </div>
    </div>
  )
}

export default ReconciliationDashboard
